import { CurrentEnvironment, EnvironmentType } from "../../../environment/envTypes"

export const BookingMessageType = {
    BOOKING_COMPLETED: "booking:completed",
    BOOKING_CANCELLED: "booking:cancelled",
    BOOKING_FAILED: "booking:failed",
    DIALOG_CLOSE: "dialog:close",
    // Manager → add-in: ask for the Outlook attendees when the user opts in at the confirm screen.
    OUTLOOK_ATTENDEES_REQUEST: "outlook:attendees:request",
    // Add-in → manager: response carrying the Outlook attendee list.
    OUTLOOK_ATTENDEES_PROVIDE: "outlook:attendees:provide",
} as const

export const BookingMessageReason = {
    DIALOG_CLOSE_BY_USER: "manager-dialog-closed-by-user",
} as const

export type BookingMessageTypeValue = (typeof BookingMessageType)[keyof typeof BookingMessageType]

export type BookingCompleted = Readonly<{
    type: typeof BookingMessageType.BOOKING_COMPLETED
    startDateTime: string
    endDateTime: string
    meetingLocation: string
    // Per-order opt-in value, sent back so the add-in can record it on the meeting/order.
    shareOutlookAttendees?: boolean
    // orderId: number
}>
export type BookingCancelled = Readonly<{
    type: typeof BookingMessageType.BOOKING_CANCELLED
    reason?: string
}>
export type BookingFailed = Readonly<{
    type: typeof BookingMessageType.BOOKING_FAILED
    reason?: string
}>
export type DialogClose = Readonly<{
    type: typeof BookingMessageType.DIALOG_CLOSE
    reason?: string
    // Per-order opt-in value, sent back on frame close so the add-in can record it (covers the service flow,
    // which has no booking-completed message).
    shareOutlookAttendees?: boolean
}>
export type OutlookAttendeesRequest = Readonly<{
    type: typeof BookingMessageType.OUTLOOK_ATTENDEES_REQUEST
}>

export type OutlookAttendeePayload = Readonly<{
    name: string
    company?: string
    email?: string
    isExternal: boolean
}>
export type OutlookAttendeesProvide = Readonly<{
    type: typeof BookingMessageType.OUTLOOK_ATTENDEES_PROVIDE
    attendees: ReadonlyArray<OutlookAttendeePayload>
}>

export type BookingResultMessage = BookingCompleted | BookingCancelled | BookingFailed | DialogClose

/** Messages the manager (dialog content) may receive FROM the add-in (the dialog parent). */
export type ParentMessage = OutlookAttendeesProvide

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null
}

function isOutlookAttendeePayload(value: unknown): value is OutlookAttendeePayload {
    if (!isRecord(value)) return false
    return (
        typeof value["name"] === "string" &&
        (value["company"] === undefined || typeof value["company"] === "string") &&
        (value["email"] === undefined || typeof value["email"] === "string") &&
        typeof value["isExternal"] === "boolean"
    )
}

function parseParentMessage(raw: unknown): ParentMessage | null {
    let value: unknown = raw
    if (typeof raw === "string") {
        try {
            value = JSON.parse(raw)
        } catch {
            return null
        }
    }
    if (!isRecord(value)) return null
    const attendees = value["attendees"]
    if (
        value["type"] === BookingMessageType.OUTLOOK_ATTENDEES_PROVIDE &&
        Array.isArray(attendees) &&
        attendees.every(isOutlookAttendeePayload)
    ) {
        return value as unknown as OutlookAttendeesProvide
    }
    return null
}

/**
 * Sends a JSON string to the Outlook dialog parent (the add-in). Mirrors the multi-path fallback used for
 * booking results: strict Office targetOrigin → loose Office → window.parent/opener postMessage (local/tunnel).
 */
async function sendJsonToOutlookParent(json: string, parentOrigin: string, currentEnv: CurrentEnvironment) {
    if (currentEnv.type !== EnvironmentType.Local) {
        await Office.onReady()
        Office.context.ui.messageParent(json, { targetOrigin: parentOrigin })
        return
    }

    const officeReady = Promise.resolve()
        .then(() => Office.onReady())
        .then(() => true)
        .catch(() => false)

    // In local/tunnel runs, Office.onReady() can hang; don't block sending forever.
    await Promise.race([officeReady, new Promise((resolve) => setTimeout(() => resolve(false), 1500))])

    // First try strict targetOrigin, then fall back to "*" if Office rejects or drops it in dev/tunnel.
    try {
        if (!Office.context?.ui?.messageParent) throw new Error("Office.context.ui.messageParent is not available")
        Office.context.ui.messageParent(json, { targetOrigin: parentOrigin })
        return
    } catch (e) {
        console.warn("sendJsonToOutlookParent: strict messageParent failed, falling back", e)
    }

    try {
        if (Office.context?.ui?.messageParent) {
            Office.context.ui.messageParent(json)
            return
        }

        const dialogOrigin = new URLSearchParams(window.location.search).get("dialogOrigin") || window.location.origin
        if (window.parent && window.parent !== window) {
            window.parent.postMessage(json, dialogOrigin)
            return
        }

        if (window.opener && typeof window.opener.postMessage === "function") {
            window.opener.postMessage(json, dialogOrigin)
            return
        }

        throw new Error("No Office.messageParent and no parent/opener to postMessage")
    } catch (e) {
        console.error("sendJsonToOutlookParent: all fallbacks failed", e)
    }
}

export async function postBookingResultToOutlook(
    msg: BookingResultMessage,
    parentOrigin: string,
    currentEnv: CurrentEnvironment,
) {
    await sendJsonToOutlookParent(JSON.stringify(msg), parentOrigin, currentEnv)
}

/** Manager → add-in: ask the add-in to deliver the Outlook attendees (used when the user opts in at confirm). */
export async function requestOutlookAttendees(parentOrigin: string, currentEnv: CurrentEnvironment) {
    const msg: OutlookAttendeesRequest = { type: BookingMessageType.OUTLOOK_ATTENDEES_REQUEST }
    await sendJsonToOutlookParent(JSON.stringify(msg), parentOrigin, currentEnv)
}

/**
 * Subscribes to messages coming FROM the add-in (the dialog parent). Registers the Office dialog parent
 * handler plus a `window.message` fallback for local/tunnel relay. Returns an unsubscribe function.
 */
export function subscribeToParentMessages(handler: (msg: ParentMessage) => void): () => void {
    // Office.context.ui exposes no typed handler removal for DialogParentMessageReceived, so we guard the
    // forwarded callback with a `disposed` flag instead of trying to remove the Office handler.
    let disposed = false

    // In the local/tunnel relay path, messages are forwarded by the add-in's dialog-redirect wrapper, whose
    // origin is passed as `dialogOrigin`. When present, only accept browser messages from that origin so a
    // random embedded iframe/tab can't inject a matching payload.
    const dialogOrigin = new URLSearchParams(window.location.search).get("dialogOrigin")

    const onWindowMessage = (event: MessageEvent) => {
        if (disposed) return
        if (dialogOrigin && event.origin !== dialogOrigin) return
        const msg = parseParentMessage(event.data)
        if (msg) handler(msg)
    }
    window.addEventListener("message", onWindowMessage)

    const onOfficeMessage = (arg: { message: string }) => {
        if (disposed) return
        const msg = parseParentMessage(arg.message)
        if (msg) handler(msg)
    }
    try {
        if (Office?.context?.ui?.addHandlerAsync) {
            Office.context.ui.addHandlerAsync(Office.EventType.DialogParentMessageReceived, onOfficeMessage)
        }
    } catch (e) {
        console.warn("subscribeToParentMessages: DialogParentMessageReceived not available", e)
    }

    return () => {
        disposed = true
        window.removeEventListener("message", onWindowMessage)
    }
}
